import React, { useEffect, useRef, useCallback } from 'react';
import { fetchWithAuth } from '../../utils/api';
import RatingEventBus from '../../services/RatingEventBus';

interface RatingStatusMonitorProps {
  roundId: string;
  componentId: string;
}

/**
 * A non-visual component that monitors rating events and handles manual refresh requests
 */
const RatingStatusMonitor: React.FC<RatingStatusMonitorProps> = ({ roundId, componentId }) => {
  const isInitialized = useRef(false);
  const componentMountedId = `${componentId}-${roundId}`;
  
  // Update the handleManualRefresh function to force using the API
  const handleManualRefresh = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      console.log("Force refreshing rating status from API");
      // Clear cache when force refreshing
      sessionStorage.removeItem(`round-${roundId}-rating-status`);
    }
    
    // When forceRefresh is true, always fetch from server
    const shouldUseAPI = forceRefresh || !isInitialized.current;
    
    if (!shouldUseAPI) {
      // Try to use cached data first if not forcing refresh
      const cachedStatus = sessionStorage.getItem(`round-${roundId}-rating-status`);
      if (cachedStatus) {
        try {
          const parsedData = JSON.parse(cachedStatus);
          // Check if cached data is recent (less than 10 seconds old)
          const lastUpdated = new Date(parsedData.last_updated || parsedData.fetch_time || 0);
          const now = new Date();
          const cacheAge = now.getTime() - lastUpdated.getTime();
          
          if (cacheAge < 10000) { // 10 seconds
            console.log("Using recent cached data");
            RatingEventBus.setRatingStatus(roundId, parsedData);
            RatingEventBus.publish('STATUS_UPDATED', { 
              ...parsedData, 
              roundId, 
              source: 'session_storage',
              timestamp: now.toISOString()
            });
            return;
          }
        } catch (e) {
          console.warn("Failed to parse or use cached status:", e);
        }
      }
    }
    
    // If we get here, we need to fetch from API
    const endpoints = [
      `/api/round/${roundId}/rating-status/`,
      `/api/rounds/${roundId}/rating-status/`,
      `/round/${roundId}/rating-status/`,
    ];
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Manual refresh: Fetching via ${endpoint}`);
        const response = await fetchWithAuth(endpoint);
        
        if (response) {
          // Update event bus
          RatingEventBus.setRatingStatus(roundId, response);
          
          // Publish the updated status
          RatingEventBus.publish('STATUS_UPDATED', { 
            ...response, 
            roundId, 
            source: 'manual_refresh',
            timestamp: new Date().toISOString()
          });
          
          // Save to session storage
          sessionStorage.setItem(`round-${roundId}-rating-status`, JSON.stringify({
            ...response,
            last_updated: new Date().toISOString()
          }));
          
          console.log('Manual refresh successful via', endpoint);
          isInitialized.current = true;
          return;
        }
      } catch (err) {
        console.warn(`Manual refresh failed with endpoint ${endpoint}:`, err);
      }
    }
    
    console.error('All manual refresh endpoints failed');
  }, [roundId]);
  
  // Initialize component with session storage data if available
  useEffect(() => {
    // Register this component
    RatingEventBus.registerComponent(componentMountedId);
    
    const initializeFromStorage = () => {
      // Try to load from session storage
      const cachedStatus = sessionStorage.getItem(`round-${roundId}-rating-status`);
      if (cachedStatus) {
        try {
          const parsedData = JSON.parse(cachedStatus);
          RatingEventBus.setRatingStatus(roundId, parsedData);
          RatingEventBus.publish('STATUS_UPDATED', { 
            ...parsedData, 
            roundId, 
            source: 'session_storage',
            timestamp: new Date().toISOString()
          });
          isInitialized.current = true;
          return true;
        } catch (e) {
          console.warn('Failed to parse cached status data:', e);
        }
      }
      return false;
    };
    
    // Try to initialize from storage
    if (!initializeFromStorage()) {
      // No storage data, do an initial fetch
      handleManualRefresh();
    }
    
    // Clean up on unmount
    return () => {
      RatingEventBus.unregisterComponent(componentMountedId);
    };
  }, [roundId, componentMountedId, handleManualRefresh]); // Add handleManualRefresh to dependency array
  
  // Update the event handler to check for forceRefresh flag
  useEffect(() => {
    const onManualRefresh = (data: any) => {
      if (data.roundId === roundId) {
        // Check if this is a force refresh request
        const isForceRefresh = data.forceRefresh === true;
        handleManualRefresh(isForceRefresh);
      }
    };
    
    // Subscribe to manual refresh events
    const unsubscribe = RatingEventBus.subscribe(
      'MANUAL_REFRESH_REQUESTED', 
      onManualRefresh, 
      componentMountedId
    );
    
    return () => {
      unsubscribe();
    };
  }, [roundId, componentMountedId, handleManualRefresh]);
  
  // This is a non-visual component
  return null;
};

export default RatingStatusMonitor;